// ============================================================
//  BreadcrumbBar.tsx — Auto breadcrumb + Back button
//  Injected in MainLayout → works on ALL pages automatically
// ============================================================
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── Route → { title, icon, module, modulePath } ─────────────
const ROUTE_MAP: Record<string, {
  title: string;
  icon: string;
  module?: string;
  modulePath?: string;
}> = {
  "/dashboard":                   { title: "Dashboard",           icon: "📊" },

  // Master Data
  "/master/bp":                   { title: "Business Partners",   icon: "🤝", module: "Master Data",  modulePath: "/dashboard" },
  "/pricing/setup":               { title: "Pricing Categories",  icon: "💰", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/commodity":           { title: "Commodity Rates",     icon: "📊", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/cost-sheet":          { title: "Cost Sheet",          icon: "🧮", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/rate-sheet":          { title: "Rate Sheet",          icon: "📋", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/overrides":           { title: "Customer Overrides",  icon: "🤝", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/dashboard":           { title: "Pricing Dashboard",   icon: "📈", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/below-cost-log":      { title: "Below-Cost Audit Log",icon: "📉", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/production-cost":     { title: "Production Cost Variance", icon: "🏭", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/bulk":                { title: "Bulk Operations",     icon: "📦", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/currency":            { title: "Currency Rates",      icon: "💱", module: "Pricing Setup", modulePath: "/dashboard" },
  "/pricing/sales-report":        { title: "Sales Performance",   icon: "📊", module: "Pricing Setup", modulePath: "/dashboard" },
  "/sales/battery-buyback":       { title: "Battery Buyback",     icon: "♻️", module: "Sales", modulePath: "/sales/orders" },
  "/tools/tally":                 { title: "Tally Importer",      icon: "📥", module: "Tools", modulePath: "/dashboard" },
  "/finance/recurring-je":        { title: "Recurring Postings",  icon: "🔁", module: "Finance", modulePath: "/dashboard" },
  "/finance/period-close":        { title: "Period-End Closing",  icon: "📅", module: "Finance", modulePath: "/dashboard" },
  "/projects":                    { title: "Project Management",  icon: "📋", module: "Tools", modulePath: "/dashboard" },
  "/admin/api-tokens":            { title: "API Tokens",          icon: "🔑", module: "Tools", modulePath: "/dashboard" },

  // Inventory
  "/inventory":                   { title: "Inventory",           icon: "📦" },
  "/inventory/items":             { title: "Item Master",          icon: "🔋", module: "Inventory",    modulePath: "/inventory" },
  "/inventory/warehouse":         { title: "Warehouses",          icon: "🏭", module: "Inventory",    modulePath: "/inventory" },
  "/inventory/grn":               { title: "Stock In (GRN)",      icon: "📥", module: "Inventory",    modulePath: "/inventory" },
  "/inventory/ledger":            { title: "Stock Ledger",        icon: "📒", module: "Inventory",    modulePath: "/inventory" },
  "/inventory/gi":                { title: "Goods Issue",         icon: "📤", module: "Inventory",    modulePath: "/inventory" },

  // Sales
  "/sales":                       { title: "Sales",               icon: "🛒" },
  "/sales/orders":                { title: "Sales Orders",         icon: "🛒", module: "Sales",        modulePath: "/sales" },
  "/sales/delivery":              { title: "Delivery",             icon: "🚚", module: "Sales",        modulePath: "/sales" },
  "/sales/ar-invoice":            { title: "AR Invoice",           icon: "🧾", module: "Sales",        modulePath: "/sales" },
  "/sales/customer-payment":      { title: "Customer Payment",     icon: "💰", module: "Sales",        modulePath: "/sales" },
  "/sales/warranty":              { title: "Battery Warranty",     icon: "🔒", module: "Sales",        modulePath: "/sales" },
  "/sales/warranty-claims":       { title: "Warranty Claims",      icon: "📋", module: "Sales",        modulePath: "/sales" },
  "/sales/dispatch-scan":         { title: "Dispatch Scan",        icon: "📡", module: "Sales",        modulePath: "/sales" },

  // Purchase
  "/purchase/orders":             { title: "Purchase Orders",      icon: "🛍️", module: "Purchase",     modulePath: "/dashboard" },
  "/purchase/grn":                { title: "Goods Receipt (GRN)",  icon: "📦", module: "Purchase",     modulePath: "/dashboard" },
  "/purchase/ap-invoice":         { title: "AP Invoice",           icon: "🧾", module: "Purchase",     modulePath: "/dashboard" },
  "/purchase/vendor-payment":     { title: "Vendor Payment",       icon: "💳", module: "Purchase",     modulePath: "/dashboard" },

  // Production
  "/production/orders":           { title: "Production Orders",    icon: "🏭", module: "Production",   modulePath: "/dashboard" },
  "/production/bom":              { title: "Bill of Materials",    icon: "📐", module: "Production",   modulePath: "/dashboard" },
  "/production/barcode":          { title: "Barcode System",       icon: "🔖", module: "Production",   modulePath: "/dashboard" },

  // Dealer Management System
  "/dealer":                      { title: "Dealer Management",    icon: "🏪" },
  "/dealer/dms":                  { title: "Dealer Management",    icon: "🏪" },

  // MIS Reports
  "/mis/reports":                 { title: "MIS Reports",          icon: "📈", module: "Finance",      modulePath: "/finance" },

  // E-Invoice
  "/sales/einvoice":              { title: "E-Invoice (IRN)",      icon: "🧾", module: "Sales",        modulePath: "/sales" },

  // Finance
  "/finance":                     { title: "Finance",              icon: "💼" },
  "/finance/coa":                 { title: "Chart of Accounts",    icon: "📂", module: "Finance",      modulePath: "/finance" },
  "/finance/opening-balance":     { title: "Opening Balance",      icon: "⚖️",  module: "Finance",      modulePath: "/finance" },
  "/finance/journal":             { title: "Journal Entry",        icon: "📒", module: "Finance",      modulePath: "/finance" },
  "/finance/reports":             { title: "Reports",              icon: "📊", module: "Finance",      modulePath: "/finance" },
  "/finance/budget":              { title: "Budget Control",       icon: "🎯", module: "Finance",      modulePath: "/finance" },
  "/finance/gl-determination":    { title: "GL Determination",     icon: "🔗", module: "Finance",      modulePath: "/finance" },

  // Admin / Settings
  "/admin/monitor":               { title: "Production Monitor",   icon: "PM", module: "Admin",        modulePath: "/dashboard" },
  "/users":                       { title: "User Management",      icon: "👥", module: "Settings",     modulePath: "/dashboard" },
  "/approval":                    { title: "Approval Engine",      icon: "✅", module: "Admin",        modulePath: "/dashboard" },
  "/audit":                       { title: "Security Dashboard",   icon: "🛡️", module: "Admin",        modulePath: "/dashboard" },
  "/settings/company":            { title: "Company Settings",     icon: "🏢", module: "Settings",     modulePath: "/dashboard" },
  "/settings/branding":           { title: "Brand Settings",       icon: "🎨", module: "Settings",     modulePath: "/dashboard" },
};

// Pages where breadcrumb should NOT show (standalone pages)
const HIDDEN_ROUTES = ["/", "/dashboard", "/login"];

const BreadcrumbBar = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const path      = location.pathname.replace(/\/$/, "") || "/";

  // Hide on dashboard / login
  if (HIDDEN_ROUTES.includes(path)) return null;

  const info = ROUTE_MAP[path];
  const title  = info?.title  || path.split("/").pop()?.replace(/-/g, " ") || "Page";
  const icon   = info?.icon   || "📄";
  const module = info?.module;

  const handleBack = () => navigate(-1);

  return (
    <div className="erp-breadcrumb-bar" style={{
      zIndex: 100,
      background: "linear-gradient(180deg, var(--erp-card-bg) 0%, var(--erp-section-bg) 100%)",
      borderBottom: "1px solid var(--erp-divider)",
      height: "var(--erp-breadcrumb-height, 40px)",
      display: "flex",
      alignItems: "center",
      padding: "0 20px",
      gap: 0,
      boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
      userSelect: "none",
      flexShrink: 0,
    }}>

      {/* ── Back Button ─────────────────────────────────────── */}
      <button
        onClick={handleBack}
        title="Go Back"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 12px 5px 8px",
          marginRight: 12,
          background: "transparent",
          border: "1px solid var(--erp-divider)",
          borderRadius: "var(--erp-radius-control)",
          cursor: "pointer",
          color: "var(--erp-text)",
          fontSize: 12,
          fontWeight: 600,
          transition: "all 0.15s",
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = "var(--erp-section-bg)";
          e.currentTarget.style.borderColor = "var(--erp-border-strong)";
          e.currentTarget.style.color = "var(--erp-primary)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "var(--erp-divider)";
          e.currentTarget.style.color = "var(--erp-text)";
        }}
      >
        {/* Left Arrow SVG */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back
      </button>

      {/* ── Divider ─────────────────────────────────────────── */}
      <div style={{ width: 1, height: 22, background: "var(--erp-divider)", marginRight: 14, flexShrink: 0 }} />

      {/* ── Breadcrumb Trail ────────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
      }}>

        {/* Home */}
        <span
          onClick={() => navigate("/dashboard")}
          style={{
            fontSize: 12,
            color: "var(--erp-muted)",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "color 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "var(--erp-primary)")}
          onMouseLeave={e => (e.currentTarget.style.color = "var(--erp-muted)")}
        >
          Home
        </span>

        {/* Module crumb */}
        {module && (
          <>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
              stroke="var(--erp-border-strong)" strokeWidth="2.5" strokeLinecap="round"
              strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            <span style={{ fontSize: 12, color: "var(--erp-muted)", whiteSpace: "nowrap" }}>
              {module}
            </span>
          </>
        )}

        {/* Current page */}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="var(--erp-border-strong)" strokeWidth="2.5" strokeLinecap="round"
          strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6"/>
        </svg>
        <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: "var(--erp-text)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {icon} {title}
        </span>

      </div>

      {/* ── Quick Module Shortcuts ───────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
        marginLeft: 16,
      }}>
        {[
          { label: "Dashboard",  path: "/dashboard",        icon: "📊" },
          { label: "Sales",      path: "/sales/orders",     icon: "🛒" },
          { label: "Purchase",   path: "/purchase/orders",  icon: "🛍️" },
          { label: "Inventory",  path: "/inventory/items",  icon: "📦" },
          { label: "Finance",    path: "/finance/reports",  icon: "💼" },
          { label: "Production", path: "/production/orders",icon: "🏭" },
        ].map(m => {
          const isActive = path.startsWith(m.path.split("/").slice(0, 2).join("/"));
          return (
            <button
              key={m.path}
              onClick={() => navigate(m.path)}
              title={m.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 9px",
                borderRadius: "var(--erp-radius-control)",
                border: isActive ? "1px solid var(--erp-primary)" : "1px solid transparent",
                background: isActive ? "var(--erp-info-bg)" : "transparent",
                color: isActive ? "var(--erp-primary)" : "var(--erp-muted)",
                fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = "var(--erp-section-bg)";
                  e.currentTarget.style.color = "var(--erp-text)";
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--erp-muted)";
                }
              }}
            >
              <span style={{ fontSize: 12 }}>{m.icon}</span>
              {m.label}
            </button>
          );
        })}
      </div>

    </div>
  );
};

export default BreadcrumbBar;
